import { ActionMenuPopover, ActionsMenuOption, Button, Chip, FormRadio, StatusChip, Typography, tryParseJSONObject } from '@fox/atoms';
import { Flex, FlexColumn, FlexColumnSpaced, FlexRow, FlexRowSpacedCentered, Inset, Padded, SpaceBetweenRow } from '@fox/layout';
import { getComponentExtension } from 'fox-extensions';
import moment from 'moment';
import React, { useState } from 'react';
import { FRIENDLY_CREDIT_CARD_TYPES, monthDictionary, paymentExtension } from '../../../constants';
import {
	CreditCardDisplayProps,
	PaymentAddProps,
	PaymentMethod,
	PaymentSelectorProps,
	PaymentType,
	ProviderCapability,
	ProviderSelection
} from '../../../contracts';
import { InfoCardContainer, InfoCardMenuContainer } from '../../shared';

export function CreditCardDisplay({
	card,
	menuOptions,
	isDefault,
	resolveProvider
}: CreditCardDisplayProps & {
	menuOptions?: ActionsMenuOption[];
}) {
	if (!card) return <InfoCardContainer>Credit Card information unavailable</InfoCardContainer>;

	const {
		type,
		maskedNumber,
		expires: { month, year }
	} = card;

	const now = moment();
	// In the month that the CC expires, the card is still good for that month, so we should be calculating
	// relative to the *end* of the month, not the start.
	const expNow = moment(`${year}-${month}-1`).endOf('month');
	const diff = now.diff(expNow, 'days');
	const isExpired = diff > 0;
	let expEl;
	let expChip;
	if (isExpired) {
		expChip = <Chip sx={{ fontSize: '0.5rem', height: '20px' }} label="EXPIRED" color="error" />;
	} else {
		expEl = (
			<Typography variant="caption">
				Expires {monthDictionary[month]} {year}
			</Typography>
		);
		if (diff < 0 && diff >= -30) {
			expChip = <Chip sx={{ fontSize: '0.5rem', height: '20px' }} label={`EXPIRES IN ${Math.abs(diff)} DAYS`} color="warning" />;
		}
	}

	return (
		<InfoCardContainer>
			<SpaceBetweenRow>
				<FlexColumn>
					<FlexRow style={{ justifyContent: 'space-between' }}>
						<Flex style={{ gap: 3 }}>
							<Typography variant="subtitle2">
								{FRIENDLY_CREDIT_CARD_TYPES[type]} {maskedNumber}
							</Typography>
							{card.inUse ? <Chip sx={{ fontSize: '0.5rem', height: '20px' }} label="In use" color="warning" /> : null}
							{expChip}
							{isDefault ? <Chip sx={{ fontSize: '0.5rem', height: '20px' }} label="Default" color="primary" /> : null}
						</Flex>
						<Flex style={{ alignItems: 'center', gap: 3 }}>
							{resolveProvider ? (
								<StatusChip
									sx={{ fontSize: '0.5rem', height: '20px' }}
									label={resolveProvider({ provider: card.provider, method: PaymentMethod.CreditCard })}
									backgroundColor="primary"
								/>
							) : null}
							{menuOptions?.length ? (
								<InfoCardMenuContainer>
									<ActionMenuPopover options={menuOptions} />
								</InfoCardMenuContainer>
							) : null}
						</Flex>
					</FlexRow>
					{expEl}
				</FlexColumn>
			</SpaceBetweenRow>
		</InfoCardContainer>
	);
}

export function AddCreditCardComponent({
	provider,
	setSelectedPayment,
	onAddPayment,
	resolveProvider,
	meta
}: {
	provider: ProviderSelection;
	setSelectedPayment: (paymentProvider: PaymentType) => void;
	onAddPayment: (paymentProvider: PaymentType) => void;
	resolveProvider?: PaymentSelectorProps['resolveProvider'];
	meta?: Record<string, unknown>;
}) {
	const [isOpened, setIsOpened] = useState(false);

	const PaymentAdd = getComponentExtension<PaymentAddProps>(
		paymentExtension(provider.providerExtension, PaymentMethod.CreditCard, 'add')
	);
	if (!PaymentAdd) return null;

	return (
		<>
			{isOpened ? (
				<Padded>
					<PaymentAdd
						onSuccess={(newCardId: number) => {
							onAddPayment({
								creditCardId: newCardId,
								method: PaymentMethod.CreditCard,
								provider: provider.provider
							});
							setSelectedPayment({
								creditCardId: newCardId,
								method: PaymentMethod.CreditCard,
								provider: provider.provider
							});
							setIsOpened(false);
						}}
						onCancel={() => setIsOpened(false)}
						provider={provider}
						settings={tryParseJSONObject(provider.settings)}
						showSave={true}
						meta={meta}
					/>
				</Padded>
			) : (
				<FlexRow>
					<Button variant="tertiary" color="primary" startIconName="Add" onClick={() => setIsOpened(true)}>
						{`Add Credit Card${resolveProvider ? ' - ' + resolveProvider(provider) : ''}`}
					</Button>
				</FlexRow>
			)}
		</>
	);
}

export const CreditCardSelector: React.FC<PaymentSelectorProps> = ({
	customers,
	provider,
	setSelectedPayment,
	onAddPayment,
	creditCardComponent,
	allowAddPaymentMethod,
	resolveProvider,
	meta
}) => {
	const CreditCardComponent = creditCardComponent ?? CreditCardDisplay;
	return (
		<FlexColumnSpaced style={{ marginTop: '1rem' }}>
			{customers
				?.flatMap(c => c.creditCards)
				?.filter(c => c.provider === provider.provider)
				?.map((c, idx) => {
					return (
						<Inset key={idx}>
							<FlexRowSpacedCentered>
								<FormRadio
									value={JSON.stringify({
										creditCardId: c.id,
										method: PaymentMethod.CreditCard,
										provider: provider.provider
									})}
									style={{ width: 40 }}
								/>
								<CreditCardComponent card={c} resolveProvider={resolveProvider} meta={meta} />
							</FlexRowSpacedCentered>
						</Inset>
					);
				})}
			{provider.capabilities?.includes(ProviderCapability.AddMethod) && allowAddPaymentMethod ? (
				<AddCreditCardComponent
					provider={provider}
					setSelectedPayment={setSelectedPayment}
					onAddPayment={onAddPayment}
					resolveProvider={resolveProvider}
					meta={meta}
				/>
			) : null}
		</FlexColumnSpaced>
	);
};
